import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Content } from '@/components/layout/wrappers'
import { useMixpanel } from '@/context/mixpanel'
import { useDebounce } from '@/hooks/useDebounce'
import { useFixedTermMarketsList } from '@/hooks/useFixedTermMarketsList'
import { type FixedTermSortBy, useFixedTermMarketsSearchState } from '@/hooks/useFixedTermMarketsSearchState'
import { type FixedTermMarketsQueryInitialData, useFixedTermMarketsQuery } from '@/queries/fixedTermMarkets.query'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { DiscoverNavigation } from './discoverNavigation'
import { FixedTermMarketsCards } from './fixedTermMarketsCards'
import { FixedTermMarketsFilters } from './fixedTermMarketsFilters'
import { FixedTermMarketsFiltersModal } from './fixedTermMarketsFiltersModal'
import { FixedTermMarketsSearchInput } from './fixedTermMarketsSearchInput'
import { FixedTermMarketsTable } from './fixedTermMarketsTable'

interface FixedTermMarketsDiscoverProps {
  initialData: FixedTermMarketsQueryInitialData
}

export const FixedTermMarketsDiscover = ({ initialData }: FixedTermMarketsDiscoverProps) => {
  const { trackEvent } = useMixpanel()
  const { data: markets } = useFixedTermMarketsQuery({ initialData })
  const allMarkets = markets ?? []
  const { filteredMarkets, pagedMarkets, currentPage, sortBy, sortOrder, setSortBy, setPage } =
    useFixedTermMarketsList(allMarkets)
  const { search } = useFixedTermMarketsSearchState()

  useDebounce(
    () => {
      if (search !== '') {
        trackEvent('Filter used', {
          Filter: 'Search',
          Value: search,
          Page: 'Discover',
          'Opportunity type': 'Fixed term',
        })
      }
    },
    [search],
    SECOND_IN_MILLISECONDS
  )

  const handleSortByChange = (nextSortBy: FixedTermSortBy) => {
    setSortBy(nextSortBy)
    trackEvent('Fixed term markets sort selected', {
      sortBy: nextSortBy,
      previousSortBy: sortBy,
      sortOrder,
      Page: 'Discover',
      'Opportunity type': 'Fixed term',
    })
  }

  return (
    <ScrollArea className="h-full">
      <Content className="flex flex-col gap-4 md:gap-6">
        <FixedTermMarketsFilters
          markets={allMarkets}
          numberOfMarkets={filteredMarkets.length}
          leadingContent={<DiscoverNavigation activePage="fixed-terms" />}
          className="hidden md:flex"
        />
        <div className="flex flex-col gap-3 md:hidden">
          <DiscoverNavigation activePage="fixed-terms" />
          <div className="flex gap-2">
            <FixedTermMarketsSearchInput
              numberOfMarkets={filteredMarkets.length}
              className="min-w-0 flex-1 [&>input]:bg-surface"
            />
            <FixedTermMarketsFiltersModal
              markets={allMarkets}
              numberOfMarkets={filteredMarkets.length}
              className="size-10 min-w-10"
            />
          </div>
        </div>
        <FixedTermMarketsTable
          markets={pagedMarkets}
          currentPage={currentPage}
          itemsCount={filteredMarkets.length}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortByChange={handleSortByChange}
          onPageChange={setPage}
          className="hidden md:block"
        />
        <FixedTermMarketsCards
          markets={pagedMarkets}
          currentPage={currentPage}
          itemsCount={filteredMarkets.length}
          onPageChange={setPage}
          className="md:hidden"
        />
      </Content>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  )
}
